import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { BreadCrumbs, Button, ConfirmationDialog, Pagination, ProtectedComponent, TextInput, UserSideBar, VerifyPatient } from "../../components";
import { TableHeader, TableRow } from "../../components/TableComponents/TableComponents";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import { useNavigate, useParams } from "react-router-dom";
import { cancelOfficePatientInvitation, resendOfficePatientInvitation, sortObj, updateOfficePatient } from "../../redux/slices/OfficeSlice";
import { searchOfficePatients } from "../../redux/slices/OfficeSlice";
import { OfficeService } from "../../services/office.service";
import { CircularProgress } from "@mui/material";
import { getSorting } from "../../utils";
import EditPatientProfile from "../../components/forms/EditPatientProfile/EditPatientProfile";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import NotFound from "../../assets/images/no-patients.png";
import { AuthPermission, UserState, authService } from "../../services/auth.service";
import InvitePatient from "../../components/forms/InvitePatient/InvitePatient";
import { startCase } from "lodash";
import { SESSION_STORAGE_PATIENT_FILTER, UserStateTypes, colors } from "../../utils/constants";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { User } from "../../models/user.model";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useDebouncedCallback } from "use-debounce";
import { OfficeState } from "../../models/office.model";
import PatientBulkInvite from "./PatientBulkInvite";
import { OfficePatientFilterType } from "./OfficePatientFilterSelect";
import OfficePatientsActions from "./OfficePatientsActions";
import { UserUpdate } from "../../models/user-update.model";
import { getParamsFromPatientFilter } from "./helpers";
import { headers } from "./constants";
import { downloadFile } from "../../utils/fileDownloads";
import { UserType } from "../../services/user.service";

const OfficePatients = ({ isAdminPage }: { isAdminPage: boolean }) => {
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { patientList, loading } = useSelector((state: RootState) => state.office);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [invitePatient, setInvitePatient] = useState(false);
  const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [sortMethods, setSortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex, setActiveSortIndex] = useState<number>(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [disableUserConfirmationOpen, setDisableUserConfirmationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [extraClasses] = useState<string[]>(headers.map(head => head.extraClasses));
  const [term, setTerm] = useState<string>("");
  const [sort, setSort] = useState({
    start: 0,
    end: 10,
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "lastName,asc",
  });
  const [showTooltip, setShowTooltip] = useState<number>(-1);
  const [selectedUserData, setselectedUserData] = useState<User | undefined>(undefined);
  const [selectedFilter, setSelectedFilter] = useState<OfficePatientFilterType>(OfficePatientFilterType.All);
  const [inviteBulkPatient, setInviteBulkPatient] = useState(false);
  const [disableReason, setDisableReason] = useState<string>("");
  const [disableReasonTouched, setDisableReasonTouched] = useState<boolean>(false);

  const getFilterParams = (filter?: OfficePatientFilterType) => {
    const prevFilter = sessionStorage.getItem(SESSION_STORAGE_PATIENT_FILTER) as OfficePatientFilterType;
    return getParamsFromPatientFilter(prevFilter || filter || selectedFilter);
  };

  const updateTable = (sortObj?: sortObj, searchText?: string, filter?: OfficePatientFilterType) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;
    const searchT = searchText ?? "";
    const { userState, hasAnnualPlan } = getFilterParams(filter);

    if (id) {
      dispatch(
        searchOfficePatients({
          officeId: id,
          term: searchT.trim().length >= 2 ? searchT.trim() : "",
          page: pageNumToBeCalled,
          size: sizeToBeCalled,
          sort: sortBy,
          userState,
          hasAnnualPlan,
        })
      ).then(() => {
        setSort({ ...sort, pageNumber: pageNumToBeCalled, rowsPerPage: sizeToBeCalled, sortedBy: sortBy });
      });
    }
  };

  const handleSort = (index: number) => {
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    setActiveSortIndex(index);
    updateTable({ sortedBy: sortMethodString, pageNumber: 0, rowsPerPage: sort.rowsPerPage }, term);
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    updateTable({ sortedBy: sort.sortedBy, pageNumber: newPage, rowsPerPage: sort.rowsPerPage }, term);
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateTable({ sortedBy: sort.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) }, term);
  };

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));
  const authorized = authService.currentUserPermissions.includes(AuthPermission.User);
  const activeToolTipOptions: string[] = ["View Profile", "Edit Profile", "Disable User"];
  const invitedToolTipOptions: string[] = ["Resend Invite", "Cancel Invite"];
  const disabledToolTipOptions: string[] = ["View Profile", "Edit Profile", "Enable User"];

  const debounced = useDebouncedCallback(value => {
    if (value && value.trim().length >= 2) {
      updateTable({ ...sort, pageNumber: 0 }, value.trim());
    }
  }, 1000);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value;
    const oldSearchTerm = term;
    setTerm(input);
    debounced(input);
    if (oldSearchTerm !== "" && input.length <= 1) {
      updateTable(sort);
    }
  };

  const handleToolTipOpen = (index: number) => {
    setShowTooltip(index);
  };

  const handleToolTipClose = () => {
    setShowTooltip(-1);
  };

  const handleRowClick = (data: User) => {
    navigate(data.userId);
  };

  const onAgreeClick = async () => {
    if (selectedUserData && id) {
      const response = await dispatch(cancelOfficePatientInvitation({ userId: selectedUserData?.userId, officeId: id }));
      if (response?.payload?.message) {
        toast?.errorToast(response?.payload?.message);
      }
      updateTable({ sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, term);
    }

    setselectedUserData(undefined);
    setConfirmationOpen(false);
  };

  const onDisagreeClick = () => {
    setConfirmationOpen(false);
    setselectedUserData(undefined);
  };

  const onDisableUserAgreeClick = () => {
    if (selectedUserData && id) {
      let newState = UserState.Active;
      if (selectedUserData?.userState === UserState.Active) {
        newState = UserState.Disabled;
      }
      // Require reason only when disabling
      if (newState === UserState.Disabled && !disableReason.trim()) {
        setDisableReasonTouched(true);
        return;
      }
      const user: UserUpdate = {
        userId: selectedUserData?.userId,
        userState: newState,
        officeId: id,
        userType: UserType.Patient,
      };
      if (newState === UserState.Disabled) {
        user.disabledReason = disableReason.trim();
      }
      dispatch(updateOfficePatient({ user, officeId: id })).then(res => {
        if (res.payload) {
          toast?.openToast(newState === UserState.Disabled ? "Patient disabled" : "Patient enabled");
        } else {
          toast?.errorToast(newState === UserState.Disabled ? "Unable to disable patient" : "Unable to enable patient");
        }
      });
    }
    setselectedUserData(undefined);
    setDisableUserConfirmationOpen(false);
    setDisableReason("");
    setDisableReasonTouched(false);
  };

  const onDisableUserDisagreeClick = () => {
    setDisableUserConfirmationOpen(false);
    setselectedUserData(undefined);
    setDisableReason("");
    setDisableReasonTouched(false);
  };

  const handleTooltipClick = async (type: string, emailId: string) => {
    const selectedUser = patientList.data?.content.find(item => item.email === emailId);
    setselectedUserData(selectedUser);
    const userId = selectedUser?.userId;
    switch (type) {
      case "Resend Invite":
        if (userId && id) {
          dispatch(resendOfficePatientInvitation({ userId: userId, officeId: id })).then(res => {
            if (res.payload) {
              toast?.openToast("User invitation sent");
            } else {
              toast?.openToast("Unable to resend invitation");
            }
          });
        }
        break;
      case "Cancel Invite":
        setConfirmationOpen(true);
        break;
      case "Edit Profile":
        if (selectedUser) setSelectedUser(selectedUser);
        break;
      case "View Profile":
        if (selectedUser) navigate(`${selectedUser.userId}`);
        break;
      case "Disable User":
      case "Enable User":
        setDisableReason("");
        setDisableReasonTouched(false);
        setDisableUserConfirmationOpen(true);
        break;
      default:
        toast?.openToast();
        break;
    }
  };

  const handleBulkInviteClose = (val: boolean) => {
    if (val) {
      updateTable(sort, term);
    }
    setInviteBulkPatient(false);
  };

  const handleChangeFilterSelect = (value: OfficePatientFilterType) => {
    setSelectedFilter(value);

    sessionStorage.setItem(SESSION_STORAGE_PATIENT_FILTER, value);
    updateTable(sort, term, value);
  };

  const handleClickDownloadCSV = () => {
    if (id) {
      const { userState, hasAnnualPlan } = getFilterParams();
      OfficeService.downloadAnnualSubscribersCSV(id, sort.sortedBy, userState, hasAnnualPlan, selectedFilter).then((blob: Blob | undefined) => {
        if (blob) {
          toast?.openToast("You will receive an email once the file is ready.");
        }
      });
    }
  };

  useEffect(() => {
    updateTable();
    const prevFilter = sessionStorage.getItem(SESSION_STORAGE_PATIENT_FILTER);
    setSelectedFilter((prevFilter as OfficePatientFilterType) || OfficePatientFilterType.All);
  }, []);

  const allowInviteActions = currentOffice?.officeState === OfficeState.Pending || currentOffice?.officeState === OfficeState.Draft;

  return (
    <>
      <main>
        <div className="container mx-auto p-8">
          {!isAdminPage ? <OfficeSectionsHeader showMeta={true} /> : <BreadCrumbs items={[{ key: "#", title: "Patients" }]} />}
          <div className="block lg:flex justify-between items-center">
            <div className="w-full">
              <div className="w-full flex flex-col gap-2 mb-5 md:mb-0 md:flex-row md:justify-between md:items-start">
                <h2 className="heading mb-0 lg:mb-5 uppercase">Patients</h2>
                {!inviteBulkPatient && (
                  <OfficePatientsActions
                    selectedFilter={selectedFilter}
                    onFilter={handleChangeFilterSelect}
                    searchQuery={term}
                    onSearch={handleSearch}
                    onClickDownloadCSV={handleClickDownloadCSV}
                    allowInviteActions={allowInviteActions}
                    showInviteActions={currentUser?.userState === UserState.Active}
                    onClickInvitePatient={() => {
                      setInviteBulkPatient(false);
                      allowInviteActions
                        ? toast?.errorToast("Unable to invite patient until your office has been published.")
                        : setInvitePatient(true);
                    }}
                    onClickBulkInvite={() => {
                      allowInviteActions
                        ? toast?.errorToast("Unable to invite patient until your office has been published.")
                        : setInviteBulkPatient(!inviteBulkPatient);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {!inviteBulkPatient ? (
            <>
              <div className="block lg:flex justify-between items-center mb-6">
                <div className="w-full">
                  <p className="text-base font-medium">
                    Easily manage all your patients in one convenient location! Send individual or bulk patient invitations to sign up with your
                    office. Manage patient profiles and view their request history.
                  </p>
                </div>
              </div>
              {loading === false ? (
                <div className="block">
                  {patientList?.data?.content && patientList?.data?.content?.length > 0 ? (
                    <>
                      <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                        {headers.map((head, index) => (
                          <TableHeader
                            title={head.title}
                            index={index}
                            key={index}
                            sortIcon={sortIcons[index]}
                            handleSort={handleSort}
                            extraClassesHeader={head.extraClasses}
                            isClickable={head.isClickable}
                          />
                        ))}
                        <div className="w-40 p-5 py-3">
                          <span className="text-base text-docsigna-purple-dark inline-block w-5"></span>
                        </div>
                      </div>
                      {patientList?.data?.content?.map((rowData, index) => {
                        const sortedData = {
                          name: `${rowData.firstName ?? "-"} ${rowData.lastName ?? ""}`,
                          email: (
                            <a href={`mailto:${rowData.email}`} className="cursor-pointer hover:underline" onClick={e => e.stopPropagation()}>
                              {rowData.email}
                            </a>
                          ),
                          Verified:
                            rowData.userState === UserState.Active ? <FiberManualRecordIcon htmlColor={`${colors.CyanBlue}`} fontSize="small" /> : "",
                          status: startCase(rowData.userState),
                          plan:
                            rowData.userAnnualPlan?.userAnnualPlanState === "active" ? (
                              <FiberManualRecordIcon htmlColor={`${colors.Lilac}`} fontSize="small" />
                            ) : rowData.annualPlanMember === true ? (
                              <FiberManualRecordIcon htmlColor={`${colors.Red}`} fontSize="small" />
                            ) : (
                              ""
                            ),
                        };
                        return (
                          <TableRow
                            data={Object.values(sortedData)}
                            key={index}
                            onRowClick={() => handleRowClick(rowData)}
                            tooltipVisible={showTooltip === index}
                            handleOpenTooltip={() => handleToolTipOpen(index)}
                            handleCloseTooltip={() => handleToolTipClose()}
                            id={rowData.email}
                            extraClassesRow={extraClasses}
                            tooltipOptions={
                              authorized
                                ? rowData.userState === UserStateTypes.Invited
                                  ? invitedToolTipOptions
                                  : rowData.userState === UserStateTypes.Active
                                  ? activeToolTipOptions
                                  : rowData.userState === UserStateTypes.Active
                                  ? disabledToolTipOptions
                                  : ["View Profile"]
                                : ["View Profile"]
                            }
                            handleTooltipClick={handleTooltipClick}
                          />
                        );
                      })}
                      <Pagination
                        colSpan={10}
                        count={patientList.data ? patientList.data.totalElements : 0}
                        onPageChange={onPageChange}
                        onRowsPerPageChange={onRowsPerPageChange}
                        page={sort.pageNumber}
                        rowsPerPage={sort.rowsPerPage}
                      />
                    </>
                  ) : (
                    <DataNotFound image={NotFound} text="No patients found" />
                  )}
                </div>
              ) : (
                <CircularProgress size={40} color={"info"} style={{ marginLeft: "50%", marginTop: "10%" }} />
              )}
            </>
          ) : id ? (
            <PatientBulkInvite key={"PatientBulkInvite"} officeId={id} setInviteBulkPatient={handleBulkInviteClose} />
          ) : null}
        </div>
        <UserSideBar
          onClickOutside={() => {
            setEditPatientOpen(false);
          }}
          title="Patient Details"
          visible={editPatientOpen}
        >
          <VerifyPatient onClickCancel={() => setEditPatientOpen(false)} fromOffice={true} setIsLoading={() => null} />
        </UserSideBar>
        {selectedUser ? (
          <UserSideBar
            onClickOutside={() => {
              !isLoading ? setSelectedUser(null) : null;
            }}
            title={`${selectedUser?.firstName ?? "Invited Patient"} ${selectedUser?.lastName ?? ""}`}
            visible={selectedUser}
          >
            <EditPatientProfile
              userData={selectedUser}
              isEditable={true}
              onClickCancel={val => {
                setSelectedUser(null);
                if (val) {
                  updateTable({ sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, term);
                }
              }}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          </UserSideBar>
        ) : null}
        {invitePatient && id ? (
          <UserSideBar
            onClickOutside={() => {
              setInvitePatient(false);
            }}
            title={`Invite Patient(s)`}
            visible={true}
            LeftbuttonText="Cancel"
            RightbuttonText="Invite"
          >
            <InvitePatient
              officeId={id}
              cancelInvite={val => {
                setInvitePatient(false);
                if (val) {
                  updateTable({ sortedBy: sort.sortedBy, pageNumber: sort.pageNumber, rowsPerPage: sort.rowsPerPage }, term);
                }
              }}
            />
          </UserSideBar>
        ) : null}
        {confirmationOpen ? (
          <ConfirmationDialog
            open={confirmationOpen}
            title={`Cancel invitation for ${selectedUserData !== undefined ? selectedUserData.email : ""}`}
            description={""}
            successButtonText={"Agree"}
            handleSuccess={onAgreeClick}
            failureButtonText={"Disagree"}
            handleFailure={onDisagreeClick}
          />
        ) : null}
        {disableUserConfirmationOpen ? (
          <ConfirmationDialog
            open={disableUserConfirmationOpen}
            description={
              selectedUserData?.userState === UserState.Active ? (
                <div className="px-1 py-2">
                  <div className="text-docsigna-blue-dark font-medium text-base py-2">{`Are you sure you want to disable this Patient?`}</div>
                  <label className="text-sm text-gray-700 block w-full mt-3 mb-2">Reason</label>
                  <TextInput
                    value={disableReason}
                    onChangeFunc={e => setDisableReason(e.currentTarget.value)}
                    onBlur={e => setDisableReason(e.currentTarget.value.trim())}
                    placeholder={"Enter reason"}
                    isRequired={true}
                    isTextArea={true}
                    extraInputClass="h-28"
                    errorMsg="Reason is required"
                    isError={disableReasonTouched && !disableReason.trim()}
                    isDarkBg={true}
                  />
                </div>
              ) : (
                `Are you sure you want to enable this Patient?`
              )
            }
            successButtonText={"Yes"}
            handleSuccess={onDisableUserAgreeClick}
            failureButtonText={"No"}
            handleFailure={onDisableUserDisagreeClick}
            disableSuccess={selectedUserData?.userState === UserState.Active && !disableReason.trim()}
          />
        ) : null}
      </main>
    </>
  );
};

export default OfficePatients;
